import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import './MesSoumissions.css';

// ══════════════════════════════════════════════════════════════
// PEFEP TECH — TABLEAU DE BORD DES SOUMISSIONS (côté client)
// Affiche les commandes / inscriptions / dons / messages captés
// par les sites du client (table `soumissions`). Lecture directe
// via Supabase (protégée par RLS : le client ne voit QUE les
// soumissions de SES sites). Aucune IA, aucun token.
// ══════════════════════════════════════════════════════════════

const TYPES = {
  commande:    { label: 'Commande',    icone: '🛒', couleur: '#00A651' },
  inscription: { label: 'Inscription', icone: '🎓', couleur: '#005AA9' },
  don:         { label: 'Don',         icone: '🤝', couleur: '#8CC63F' },
  reservation: { label: 'Réservation', icone: '📅', couleur: '#F59E0B' },
  demande:     { label: 'Demande',     icone: '📩', couleur: '#6366F1' },
  contact:     { label: 'Message',     icone: '✉️', couleur: '#94A3B8' },
};

function infoType(t) {
  return TYPES[t] || TYPES.contact;
}

function normaliserTel(t) {
  if (!t) return '';
  let n = String(t).replace(/\D/g, '');
  if (n.length === 8) n = '509' + n;
  return n;
}

export default function MesSoumissions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [sites, setSites] = useState([]);
  const [soumissions, setSoumissions] = useState([]);
  const [filtreType, setFiltreType] = useState('tous');
  const [filtreSite, setFiltreSite] = useState('tous');
  const [recherche, setRecherche] = useState('');

  const charger = async () => {
    if (!user?.id) return;
    setChargement(true);
    setErreur('');
    try {
      const { data: dataSites } = await supabase
        .from('sites_generes')
        .select('id, nom_organisation')
        .eq('client_id', user.id);

      const { data: dataSoum, error } = await supabase
        .from('soumissions')
        .select('*')
        .order('cree_le', { ascending: false });

      if (error) throw error;

      setSites(dataSites || []);
      setSoumissions(dataSoum || []);
    } catch (e) {
      setErreur('Impossible de charger vos soumissions. ' + (e.message || ''));
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const nomSite = (id) => {
    const s = sites.find(x => x.id === id);
    return s ? s.nom_organisation : 'Mon site';
  };

  const marquerTraite = async (id, statutActuel) => {
    const nouveau = statutActuel === 'traite' ? 'nouveau' : 'traite';
    const { error } = await supabase
      .from('soumissions')
      .update({ statut: nouveau })
      .eq('id', id);
    if (error) { alert('Erreur : ' + error.message); return; }
    setSoumissions(prev => prev.map(s => (s.id === id ? { ...s, statut: nouveau } : s)));
  };

  const filtrees = soumissions.filter(s => {
    if (filtreType !== 'tous' && s.type_action !== filtreType) return false;
    if (filtreSite !== 'tous' && s.site_id !== filtreSite) return false;
    if (recherche) {
      const r = recherche.toLowerCase();
      const blob = `${s.nom || ''} ${s.telephone || ''} ${s.email_visiteur || ''} ${s.item_titre || ''}`.toLowerCase();
      if (!blob.includes(r)) return false;
    }
    return true;
  });

  const nbNouveaux = soumissions.filter(s => s.statut === 'nouveau').length;
  const compteType = (t) => soumissions.filter(s => s.type_action === t).length;

  if (chargement) {
    return (
      <div className="soum-loading">
        <div className="soum-spinner"></div>
        <p>Chargement de vos demandes...</p>
      </div>
    );
  }

  return (
    <div className="soum-container">
      <button type="button" className="soum-retour" onClick={() => navigate('/tableau-de-bord')}>
        ← Retour au tableau de bord
      </button>
      <div className="soum-header">
        <div>
          <h2>📥 Mes demandes reçues</h2>
          <p className="soum-sous">Commandes, inscriptions, dons et messages laissés sur vos sites.</p>
        </div>
        <button type="button" className="soum-btn-refresh" onClick={charger}>🔄 Actualiser</button>
      </div>

      {erreur && <div className="soum-erreur">{erreur}</div>}

      {/* Stats rapides */}
      <div className="soum-stats">
        <div className="soum-stat">
          <div className="soum-stat-val">{soumissions.length}</div>
          <div className="soum-stat-label">Total</div>
        </div>
        <div className="soum-stat soum-stat-nouveau">
          <div className="soum-stat-val">{nbNouveaux}</div>
          <div className="soum-stat-label">Nouvelles</div>
        </div>
        {Object.keys(TYPES).filter(t => compteType(t) > 0).map(t => (
          <div className="soum-stat" key={t}>
            <div className="soum-stat-val">{compteType(t)}</div>
            <div className="soum-stat-label">{infoType(t).icone} {infoType(t).label}</div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="soum-filtres">
        <input
          type="text"
          className="soum-recherche"
          placeholder="🔍 Rechercher (nom, téléphone, e-mail, article...)"
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
        />
        <div className="soum-filtres-boutons">
          <button type="button" className={`soum-filtre-btn ${filtreType === 'tous' ? 'actif' : ''}`} onClick={() => setFiltreType('tous')}>
            Tous les types
          </button>
          {Object.keys(TYPES).filter(t => compteType(t) > 0).map(t => (
            <button
              type="button"
              key={t}
              className={`soum-filtre-btn ${filtreType === t ? 'actif' : ''}`}
              onClick={() => setFiltreType(t)}
            >
              {infoType(t).icone} {infoType(t).label}
            </button>
          ))}
        </div>
        {sites.length > 1 && (
          <select className="soum-select-site" value={filtreSite} onChange={e => setFiltreSite(e.target.value)}>
            <option value="tous">Tous mes sites</option>
            {sites.map(s => (
              <option key={s.id} value={s.id}>{s.nom_organisation}</option>
            ))}
          </select>
        )}
      </div>

      {/* Liste */}
      {filtrees.length === 0 ? (
        <div className="soum-empty">
          <div className="soum-empty-icone">📭</div>
          <h3>Aucune demande pour le moment</h3>
          <p>Dès qu'un visiteur remplit un formulaire sur votre site, sa demande apparaît ici.</p>
        </div>
      ) : (
        <div className="soum-liste">
          {filtrees.map(s => {
            const t = infoType(s.type_action);
            const tel = normaliserTel(s.telephone);
            const donnees = (s.donnees && typeof s.donnees === 'object') ? s.donnees : {};
            const date = s.cree_le
              ? new Date(s.cree_le).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
              : '';
            const estNouveau = s.statut === 'nouveau';

            return (
              <div key={s.id} className={`soum-carte ${estNouveau ? 'soum-carte-nouveau' : ''}`}>
                <div className="soum-carte-entete">
                  <span className="soum-badge" style={{ background: t.couleur }}>{t.icone} {t.label}</span>
                  {estNouveau && <span className="soum-pastille">Nouveau</span>}
                  <span className="soum-date">{date}</span>
                </div>

                {s.item_titre && <div className="soum-item">{s.item_titre}</div>}
                <div className="soum-site">🌐 {nomSite(s.site_id)}</div>

                <div className="soum-contact">
                  <div className="soum-contact-nom">👤 {s.nom}</div>
                  {s.telephone && <div className="soum-contact-ligne">📞 {s.telephone}</div>}
                  {s.email_visiteur && <div className="soum-contact-ligne">✉️ {s.email_visiteur}</div>}
                </div>

                {Object.keys(donnees).length > 0 && (
                  <div className="soum-details">
                    {Object.keys(donnees).map(cle => (
                      <div className="soum-detail-ligne" key={cle}>
                        <span className="soum-detail-cle">{cle}</span>
                        <span className="soum-detail-val">{String(donnees[cle])}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="soum-actions">
                  {tel && (
                    <button
                      type="button"
                      className="soum-action soum-action-wa"
                      onClick={() => window.open('https://wa.me/' + tel, '_blank', 'noopener,noreferrer')}
                    >
                      💬 WhatsApp
                    </button>
                  )}
                  {s.email_visiteur && (
                    <button
                      type="button"
                      className="soum-action soum-action-mail"
                      onClick={() => { window.location.href = 'mailto:' + s.email_visiteur; }}
                    >
                      ✉️ E-mail
                    </button>
                  )}
                  <button
                    type="button"
                    className={`soum-action ${estNouveau ? 'soum-action-traiter' : 'soum-action-rouvrir'}`}
                    onClick={() => marquerTraite(s.id, s.statut)}
                  >
                    {estNouveau ? '✅ Marquer traité' : '↩ Rouvrir'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}